import { getUidOfKey, interpolateColor } from '../../CommonFunctions/CommonFunctions';
import { palette } from '../../..';
import { formatNumberWithAbbrev } from '../../ChartOptions/Format/NumberFormatter';
import numbro from 'numbro';

export const setCellColor = (
  isHeader: boolean,
  crossTabData: any,
  colIndex: number,
  rowIndex: number,
  colData: any,
  chartProperties: any,
  propKey: string,
  chartControls: any,
  chartThemes: any,
  isDark: boolean,
  dustbinRows?: any,
): object => {
  try {
    let isConditionAvailable = checkChartControlConditionalColor(
      chartProperties,
      propKey,
      chartControls,
    );
    let _conditionalStyle: any =
      getConditionalFormat(
        crossTabData,
        colIndex,
        rowIndex,
        colData,
        chartProperties,
        propKey,
        chartControls,
        dustbinRows,
      ) || {};
    let _crossTabStyle = isHeader
      ? chartControls.properties[propKey].crossTabHeaderLabelOptions
      : chartControls.properties[propKey].crossTabCellLabelOptions;
    let style = {
      color:
        isConditionAvailable && _conditionalStyle.fontColor
          ? _conditionalStyle.fontColor
          : _crossTabStyle.labelColorManual
            ? _crossTabStyle.labelColor
            : isDark
              ? '#fff'
              : _crossTabStyle.labelColor,
      fontWeight:
        isConditionAvailable && _conditionalStyle.isBold
          ? _conditionalStyle.isBold
            ? 'bold'
            : 'normal'
          : _crossTabStyle.fontWeight,
      fontSize: _crossTabStyle.fontSize,
      borderRightWidth: chartControls.properties[propKey].crossTabStyleOptions.borderWidth,
      borderBottomWidth: chartControls.properties[propKey].crossTabStyleOptions.borderWidth,
      backgroundColor:
        isConditionAvailable && _conditionalStyle.backgroundColor
          ? _conditionalStyle.backgroundColor
          : isHeader
            ? chartThemes[0].tableHeaderBackground
            : chartThemes[0].background,
      textDecoration: _conditionalStyle.isUnderlined ? 'underline' : '',
      fontStyle: _conditionalStyle.isItalic ? 'italic' : 'normal',
    };

    return style;
  } catch (err) {
    console.error(err);
    return {};
  }
};

const checkChartControlConditionalColor = (
  chartProperties: any,
  propKey: string,
  chartControls: any,
): boolean => {
  switch (chartProperties.properties[propKey].chartType) {
    case 'table':
      return chartControls.properties[propKey]?.tableConditionalFormats?.length > 0;
    case 'crossTab':
      return chartControls.properties[propKey]?.tableConditionalFormats?.length > 0;
  }

  return false;
};

const getConditionalFormat = (
  crossTabData: any,
  colIndex: number,
  rowIndex: number,
  colData: any,
  chartProperties: any,
  propKey: string,
  chartControls: any,
  dustbinRows?: any,
) => {
  try {
    let _colNameConditions: any = {};
    let colName = getColumnName(crossTabData, colIndex, chartProperties, propKey);
    let currentCellData = crossTabData[rowIndex].columnItems[colIndex];

    let columnId =
      currentCellData.fieldUId ||
      getUidOfKey(chartProperties.properties[propKey].chartAxes, colName);

    //-----------When we have column and row and don't have anything in measure then both currentCellData.isHeaderField and currentCellData.isRowField for row are false(according to current implementation). So here we are finding whether it is row field or not from dustbinRows. -----------//
    if (dustbinRows && dustbinRows.length > 0 && colIndex < dustbinRows.length) {
      colName = dustbinRows[colIndex].fieldname;
      columnId =
        dustbinRows[colIndex].uId ||
        getUidOfKey(chartProperties.properties[propKey].chartAxes, colName);
    }

    /*  PRS 16Nov2024   */
    let symbol = chartControls.properties[propKey].formatOptions.labelFormats.currencySymbol;

    if (
      !(currentCellData.isHeaderField || currentCellData.isRowField) &&
      currentCellData.displayData !== '(Blank)'
    ) {
      colData = colData
        ?.toString()
        ?.replaceAll(',', '')
        .replaceAll('%', '')
        .replace(symbol, '')
        .replace(/^[^\d\-\+\.KMBTkmbt]+/, '')
        .trim();
    }

    let _tableConditionalFormats = JSON.parse(
      JSON.stringify(chartControls.properties[propKey].tableConditionalFormats),
    );

    let colNameConditions = _tableConditionalFormats?.find((item: any) => {
      return item.columnId === columnId;
    });

    if (colNameConditions) _colNameConditions = JSON.parse(JSON.stringify(colNameConditions));

    if (Object.keys(_colNameConditions).length > 0) {
      if (_colNameConditions?.isGradient) {
        let currentCellData = crossTabData[rowIndex].columnItems[colIndex];

        if (!currentCellData.isHeaderField) {
          return getGradientBasedStyle(_colNameConditions, crossTabData, rowIndex, colData);
        } else {
          return {};
        }
      } else {
        //Rule Based
        // let currentCellData = crossTabData[rowIndex].columnItems[colIndex];

        // /*  PRS 16Nov2024   */
        // let symbol = chartControls.properties[propKey].formatOptions.labelFormats.currencySymbol;

        // if (!(currentCellData.isHeaderField || currentCellData.isRowField)) {
        //   colData = colData
        //     ?.toString()
        //     ?.replaceAll(',', '')
        //     .replaceAll('%', '')
        //     .replace(symbol, '')
        //     .replace(/^[^\d\-\+\.KMBTkmbt]+/, '')
        //     .trim();
        // }

        //----check if it is a row field cell either by flag(currentCellData.isRowField) or by position using dustbinRows-----//
        const isRowFieldCell =
          currentCellData.isHeaderField ||
          currentCellData.isRowField ||
          (dustbinRows && dustbinRows.length > 0 && colIndex < dustbinRows.length);

        if (isRowFieldCell) {
          const matchedCondition = _colNameConditions.value.find((item: any) => {
            const cellValue = colData?.toString();
            const ruleValue = item.colValue?.toString();

            if (ruleValue === cellValue) {
              return true;
            }

            if (cellValue === '(Blank)') {
              return (
                ruleValue === '' ||
                ruleValue === 'Blank' ||
                item.colValue === null ||
                item.colValue === undefined ||
                ruleValue === '(Blank)'
              );
            }

            return false;
          });
          return matchedCondition;
        } else {
          if (!isNaN(colData) && !isNaN(colData.toString().substring(colData.length - 1))) {
            let lastSatisfiedCondition = getLastSatisfiedCondition(colData, _colNameConditions);
            return lastSatisfiedCondition || {};
          } else if (
            ['K', 'M', 'B', 'T'].includes(colData.toString().substring(colData.length - 1))
          ) {
            let lastSatisfiedCondition = getLastSatisfiedCondition(
              getActualNumber(colData),
              _colNameConditions,
            );
            return lastSatisfiedCondition || {};
          } else {
            return {};
          }
        }
      }
    } else {
      return {};
    }
  } catch (err) {
    console.error(err);
  }
};

const getGradientBasedStyle = (
  _colNameConditions: any,
  crossTabData: any,
  rowIndex: number,
  colData: any,
) => {
  let startStyle: any = {},
    midStyle: any = {},
    endStyle: any = {};

  if (colData === '' || colData === '(Blank)') {
    const nullStyle = _colNameConditions.value.find((item: any) => item.name === 'Null');
    return nullStyle;
  }

  if (_colNameConditions.value.length === 4) {
    midStyle = _colNameConditions.value.find((item: any) => item.name?.trim() === 'Mid Value');
  }

  startStyle = _colNameConditions.value.find((item: any) => item.name === 'Min');
  endStyle = _colNameConditions.value.find((item: any) => item.name === 'Max');

  return checkColumnValueForGradient(startStyle, midStyle, endStyle, crossTabData, colData);
};

const checkColumnValueForGradient = (
  startStyle: any,
  midStyle: any,
  endStyle: any,
  crossTabData: any,
  colData: any,
) => {
  let _colValue: Number = 0;

  if (isNaN(colData?.toString().substring(0, 1))) {
    if (colData?.toString().substring(0, 1) !== '-') {
      return {};
    } else {
      _colValue = Number(colData); // negative value case
    }
  } else if (!isNaN(colData) && !isNaN(colData?.toString().substring(colData.length - 1))) {
    _colValue = Number(colData);
  } else if (['K', 'M', 'B', 'T'].includes(colData?.toString().substring(colData.length - 1))) {
    _colValue = Number(getActualNumber(colData));
  }
  if (Number(_colValue) < Number(startStyle.value)) {
    return {};
  } else if (Number(_colValue) > Number(endStyle.value)) {
    return {};
  } else {
    let stepNumber = Math.min(Number(endStyle.value) - Number(startStyle.value) + 1, 20);

    if (Object.keys(midStyle).length > 0) {
      let step: number = 0;
      let colors: any = [];
      let style: any = {};

      if (Number(midStyle.value) < Number(_colValue)) {
        stepNumber = Math.min(Number(endStyle.value) - Number(midStyle.value) + 1, 20);
        step =
          calculateStep(
            Number(midStyle.value),
            Number(endStyle.value),
            stepNumber,
            Number(_colValue),
          ) || 0;
        colors = interpolateColor(midStyle?.backgroundColor, endStyle?.backgroundColor, stepNumber);

        style = {};

        if ((step || 0) < stepNumber / 2) {
          style = midStyle;
        } else {
          style = endStyle;
        }

        if (colors[step || 0]) {
          style.backgroundColor = colors[step || 0];
        }

        return style;
      } else {
        stepNumber = Math.min(Number(midStyle.value) - Number(startStyle.value) + 1, 20);
        step =
          calculateStep(
            Number(startStyle.value),
            Number(midStyle.value),
            stepNumber,
            Number(_colValue),
          ) || 0;
        colors = interpolateColor(
          startStyle?.backgroundColor,
          midStyle?.backgroundColor,
          stepNumber,
        );

        style = {};

        if ((step || 0) < stepNumber / 2) {
          style = startStyle;
        } else {
          style = midStyle;
        }

        if (colors[step || 0]) {
          style.backgroundColor = colors[step || 0];
        }

        return style;
      }
    } else {
      let step = calculateStep(
        Number(startStyle.value),
        Number(endStyle.value),
        stepNumber,
        Number(_colValue),
      );
      let colors = interpolateColor(
        startStyle?.backgroundColor,
        endStyle?.backgroundColor,
        stepNumber,
      );
      let style: any = {};
      if ((step || 0) < stepNumber / 2) {
        style = startStyle;
      } else {
        style = endStyle;
      }

      if (colors[step || 0]) {
        style.backgroundColor = colors[step || 0];
      }

      return style;
    }
  }
};

const calculateStep = (minValue: number, maxValue: number, stepSize: number, value: number) => {
  if (stepSize <= 0) {
    throw new Error('Step size must be greater than 0.');
  }

  const range = maxValue - minValue;
  const steps = Math.ceil(range / stepSize);
  if (minValue === maxValue && maxValue === value) {
    return 1;
  }
  for (let i = 1; i <= stepSize; i++) {
    if (minValue + steps * i >= value) {
      return i - 1;
    }
  }
};

const getActualNumber = (colData: string) => {
  try {
    if (!colData) return 0;

    const text = colData.toString().trim().toUpperCase();

    const lastChar = text.slice(-1);

    const multipliers: any = {
      K: 1e3,
      M: 1e6,
      B: 1e9,
      T: 1e12,
    };

    if (multipliers[lastChar]) {
      const numericPart = numbro.unformat(text.slice(0, -1));
      return numericPart * multipliers[lastChar];
    }

    return numbro.unformat(text);
  } catch (err) {
    console.error(err);
    return Number(colData);
  }
};

export const getMinMaxForFormattedData = (minValue: string, maxValue: string) => {
  const formattedMin = formatNumberWithAbbrev(minValue, 1);
  const formattedMax = formatNumberWithAbbrev(maxValue, 1);
  const formattedMinInNumber = getActualNumber(formattedMin.toString())?.toString();
  const formattedMaxInNumber = getActualNumber(formattedMax.toString())?.toString();
  return { formattedMinInNumber, formattedMaxInNumber };
};

const getLastSatisfiedCondition = (value: any, colNameConditions: any) => {
  for (let i = colNameConditions.value.length - 1; i >= 0; i--) {
    let item = colNameConditions.value[i];

    if (
      checkNumberAgaintConditionType(
        item.conditionType,
        item.target,
        value,
        item.minValue,
        item.maxValue,
      )
    ) {
      return colNameConditions.value[i];
    }
  }
};

const checkNumberAgaintConditionType = (
  conditionType: number,
  target: any,
  value: number,
  minValue: number,
  maxValue: number,
): boolean => {
  let result = false;

  if (target === null || target?.trim() === '') {
    return false;
  }

  value = Number(value);
  target = Number(target);

  switch (conditionType) {
    case 1: //greater than
      result = value > target;
      break;
    case 2: //less than
      result = value < target;
      break;
    case 3: //>=
      result = value >= target;
      break;
    case 4: //<= than
      result = value <= target;
      break;
    case 5: //=
      result = value === target;
      break;
    case 6: //<>
      result = value !== target;
      break;
    case 7: //between
      if (minValue <= value && value <= maxValue) {
        result = true;
      } else {
        result = false;
      }

      break;
    default:
      result = false;
      break;
  }
  return result;
};

const getColumnName = (
  crossTabData: any,
  colIndex: number,
  chartProperties: any,
  propKey: string,
): string => {
  switch (chartProperties.properties[propKey].chartType) {
    case 'table':
      return crossTabData[0]?.columnItems[colIndex]?.displayData;
    case 'crossTab':
      let headerIndex =
        chartProperties.properties[propKey].chartAxes.find((item: any) => item.name === 'Column')
          ?.fields?.length || 0;
      return crossTabData[headerIndex]?.columnItems[colIndex]?.displayData;
  }

  return '';
};
